import { useMutation, UseMutationOptions, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { buildErrorMessage } from 'utils'
import {
  InformalFeedbackRequestsClient,
  InformalFeedbackReview,
  InformalFeedbackReviewDismissReason,
} from 'api/clients/InformalFeedbackRequestsClient'
import { workspaceTasksKeys } from 'screens/Todos/hooks/useWorkspaceTasks'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useRefreshInformalFeedbackRequests } from 'hooks/useRefreshInformalFeedbackRequests'

interface Props {
  id: number
  requestId?: number
  almanacFileId?: string
}

export interface Data {
  informal_feedback_review: InformalFeedbackReview
}

export interface Attributes {
  message?: string
  reason: InformalFeedbackReviewDismissReason
}

export const useDismissReview = (
  { id, requestId, almanacFileId }: Props,
  options?: Omit<UseMutationOptions<Data, unknown, Attributes>, 'mutationKey' | 'mutationFn'>,
) => {
  const queryClient = useQueryClient()
  const currentWorkspaceKey = useCurrentWorkspace()?.key
  const { refreshRequests } = useRefreshInformalFeedbackRequests({
    almanacFileId,
    requestId,
  })

  return useMutation<Data, unknown, Attributes>(
    'dismissReview',
    async ({ message, reason }) => {
      const response = await InformalFeedbackRequestsClient.dismissReview(id, {
        message,
        reason,
        include_request: true,
      })
      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error(
          buildErrorMessage(response.data.errors) ||
            'An error occurred! Our team has been notified and we are looking into it.',
        )
      }
      return response.data
    },
    {
      ...options,
      onSuccess: async (data, variables, context) => {
        refreshRequests()
        if (currentWorkspaceKey) queryClient.invalidateQueries(workspaceTasksKeys.navMenuCounter(currentWorkspaceKey))
        options?.onSuccess?.(data, variables, context)
      },
    },
  )
}
